import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { AppConstants } from '@core/constants';
import { ApiBaseService } from '@core/services/api-base.service';
import { IIntegrationLink } from '@shared/interfaces/integration-link.interface';
import { IIntegrationTemplate } from '@shared/interfaces/integration-template.interface';
import { String } from 'typescript-string-operations';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { BehaviorSubject } from 'rxjs';

const COUPONS: Array<{
  code: string,
  discount_percent: number,
  condition?: string
}> = [
  {
    code: "INSCYD3",
    discount_percent: 3,
  },
  {
    code: "INSCYD6-25BOXES",
    discount_percent: 6,
    condition: ">= 25 boxes of test strips",
  },
  {
    code: "INSCYD9-50BOXES",
    discount_percent: 9,
    condition: ">= 50 boxes of test strips",
  },
];

@Component({
  selector: 'app-integration-cardioworld',
  templateUrl: './integration-cardioworld.component.html',
  styleUrls: ['./integration-cardioworld.component.scss']
})
export class IntegrationCardioworldComponent implements OnInit {
  @Input() public template!: IIntegrationTemplate | undefined;
  @Input() public integration: IIntegrationLink | undefined;

  public showIntegrationActiveFlag$ = new BehaviorSubject(false);
  public integrationForm!: FormGroup;
  public coupons = COUPONS;
  public displayedColumns: string[] = ['code'];

  constructor(
    private fb: FormBuilder,
    private apiService: ApiBaseService,
    private snackBar: MatSnackBar,
  ) { }

  ngOnInit(): void {
    this.initForm();
    this.populateForm();
  }

  private initForm() {
    this.integrationForm = this.fb.group({
      email: new FormControl<string | null>(null, [Validators.required, Validators.email]),
      is_active: new FormControl<boolean>(true),
    });
  }

  private populateForm() {
    if (!this.integration) {
      return
    }

    this.showIntegrationActiveFlag$.next(true);
    this.integrationForm.patchValue({
      is_active: this.integration.is_active,
      email: this.integration.configuration?.email,
    })
  }

  public getSaveButtonText(): string {
    if (!this.integration) {
      return "Activate"
    }

    return "Save"
  }

  public getActiveIconText(): string {
    if (!this.integration) {
      return "Inactive"
    }

    return this.integration.is_active ? "Active" : "Inactive";
  }

  public getToggleActivationButtonText(): string {
    if (!this.integration) {
      // Doesn't matter - the button will be hidden anyway
      return "Activate"
    }

    return this.integration.is_active ? "Deactivate" : "Activate";
  }

  public getActiveIconClass(): string {
    if (!this.integration) {
      return "inactive"
    }

    return this.integration.is_active ? "active" : "inactive";
  }

  public canSave(): boolean {
    return this.integrationForm.valid;
  }

  public toggleActivation(): void {
    this.integrationForm.patchValue({
      is_active: !this.integrationForm.get("is_active")?.value
    });

    if (this.integration != null) {
      return this.update(this.integration.id, {
        configuration: {
          email: this.integrationForm.get("email")?.value,
        },
        is_active: this.integrationForm.get("is_active")?.value,
      });
    }
  }

  public showDiscountCoupons(): boolean {
    return this.integration != null && this.integration.is_active;
  }

  public save() {
    const saveData: Record<string, any> = {
      configuration: {
        email: this.integrationForm.get("email")?.value,
      },
      is_active: this.integrationForm.get("is_active")?.value,
    };

    if (this.integration != null) {
      return this.update(this.integration.id, saveData);
    }

    saveData.template = this.template?.id;
    return this.create(saveData);
  }

  private update(id: number, data: Record<string, any>) {
    const apiUrl: string = String.Format(AppConstants.API.INTEGRATIONS.LINKS.UPDATE, id);
    this.apiService.patchJson<IIntegrationLink>(apiUrl, data).subscribe({
      next: (response) => {
        this.integration = response;
        return this.snackBar.open("Cardioworld integration updated successfully", 'OK', AppConstants.TOAST_CONFIG.SUCCESS);
      },
      error: (err) => {
        return this.snackBar.open(err, 'OK', AppConstants.TOAST_CONFIG.ERROR);
      }
    });
  }

  private create(data: Record<string, any>) {
    this.apiService.postJson<IIntegrationLink>(AppConstants.API.INTEGRATIONS.LINKS.CREATE, data).subscribe({
      next: (response) => {
        this.integration = response;
        this.showIntegrationActiveFlag$.next(true);
        return this.snackBar.open("Cardioworld integration created successfully", 'OK', AppConstants.TOAST_CONFIG.SUCCESS);
      },
      error: (err) => {
        return this.snackBar.open(err, 'OK', AppConstants.TOAST_CONFIG.ERROR);
      }
    });
  }
}
